import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import {
  Badge,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  ListItem,
  Menu,
  MenuItem,
  MenuList,
  Typography,
} from "@mui/material";
import { useCompactScreen } from "@shared/frontend/hooks/use-compact-screen.hook";
import { useMuiMenu } from "@shared/frontend/hooks/use-mui-menu.hook";
import type { Column } from "@tanstack/react-table";
import React, { useMemo } from "react";
import { useHyperTable } from "../../HyperTable";
import { CheckboxIcon } from "@shared/frontend/icons";
import { OverrideViewOption } from "../../types";
import {
  toolbarButtonSx,
  toolbarCompactBadgeStyle,
  toolbarMenuPaperSx,
  toolbarWideBadgeStyle,
} from "./toolbar-button-styles";

export function ToolbarViewOptions<Entity>(props: { overrideViewOptions?: OverrideViewOption<Entity>[], onOverrideViewOptionsChange?: (overrideViewOptions: OverrideViewOption<Entity>[]) => void }) {
  const { overrideViewOptions = [], onOverrideViewOptionsChange } = props;
  const [btnRef, menuProps, menuActions] = useMuiMenu<HTMLButtonElement>();
  const isCompact = useCompactScreen();

  const { table } = useHyperTable<Entity>();

  const orderedColumns = useMemo(
    () =>
      table.getAllColumns().sort((a, b) => {
        const { columnPinning } = table.getState();
        const { left: right = [], right: left = [] } = columnPinning; //Switching because of direction RTL

        function getPin(f: Column<Entity, unknown>) {
          return left.includes(f.id)
            ? "left"
            : right.includes(f.id)
              ? "right"
              : false;
        }

        const aPin = getPin(a);

        const isEqual = getPin(a) === getPin(b);

        if (isEqual && aPin === false) {
          return a.getIndex() - b.getIndex();
        }
        if (isEqual && aPin === "left") {
          return left.indexOf(a.id) - left.indexOf(b.id);
        }
        if (isEqual && aPin === "right") {
          return right.indexOf(a.id) - right.indexOf(b.id);
        }
        return aPin ? -1 : 1;
      }),
    [table.getAllColumns(), table.getState().columnPinning],
  );

  const isOverride = useMemo(() => {
    return overrideViewOptions.length > 0;
  }, [overrideViewOptions]);

  const isAllOverrideViewOptionsVisible = useMemo(() => {
    return isOverride && overrideViewOptions.every((option) => option.viewed);
  }, [isOverride, overrideViewOptions]);

  const badgeStyle = isCompact ? toolbarCompactBadgeStyle : toolbarWideBadgeStyle;

  return (
    <Badge
      overlap="circular"
      anchorOrigin={{ horizontal: "left", vertical: "top" }}
      slotProps={{
        badge: {
          style: {
            color: "white",
            ...badgeStyle,
          },
        },
      }}
      color="secondary"
      badgeContent={
        (isOverride ? isAllOverrideViewOptionsVisible : table.getIsAllColumnsVisible()) ? (
          0
        ) : (
          <VisibilityOffOutlinedIcon
            fontSize="small"
            sx={{
              maxWidth: "15px",
            }}
          />
        )
      }
    >
      <Button
        aria-label="Toggle columns"
        ref={btnRef}
        onClick={menuActions.toggle}
        variant="outlined"
        color="secondary"
        sx={toolbarButtonSx}
      >
        <TuneRoundedIcon />
        תצוגה
      </Button>
      <Menu {...menuProps} slotProps={{ paper: { sx: toolbarMenuPaperSx } }}>
        <MenuList>
          <ListItem>
            <Typography color="primary" pt={0.1} fontWeight={600}>
              תצוגת עמודות
            </Typography>
          </ListItem>
          <Divider sx={{ mt: "1px" }} />
          <FormGroup>
            {isOverride ? overrideViewOptions.map((option) => {
              return (
                <MenuItem key={option.option} sx={{ display: "flex" }}>
                  <FormControlLabel
                    sx={{
                      height: "100%",
                    }}
                    componentsProps={{
                      typography: {
                        sx: {
                          pb: 0.5,
                        },
                      },
                    }}
                    label={option.label}
                    control={
                      <Checkbox
                        icon={<CheckboxIcon />}
                        checkedIcon={<CheckboxIcon checked />}
                      />
                    }
                    checked={option.viewed}
                    onChange={(_, value) => {
                      const newOverrideViewOptions = overrideViewOptions.map((o) => {
                        if (o.option === option.option) {
                          return { ...o, viewed: value };
                        }
                        return o;
                      });
                      onOverrideViewOptionsChange?.(newOverrideViewOptions);
                    }}
                  />
                </MenuItem>
              );
            }) : orderedColumns.map((column) => {
              const showInViewOptions = !column.columnDef.dontShowInViewOptions;
              const caption = column.columnDef.meta?.caption;
              if (!caption || !showInViewOptions || !column.getCanHide())
                return null;
              return (
                <MenuItem key={column.id} sx={{ display: "flex" }}>
                  <FormControlLabel
                    sx={{
                      height: "100%",
                    }}
                    componentsProps={{
                      typography: {
                        sx: {
                          pb: 0.5,
                        },
                      },
                    }}
                    label={typeof caption === "function" ? caption() : caption}
                    control={
                      <Checkbox
                        icon={<CheckboxIcon />}
                        checkedIcon={<CheckboxIcon checked />}
                        onChange={(_, value) => column.toggleVisibility(value)}
                        checked={column.getIsVisible()}
                      />
                    }
                  />
                </MenuItem>
              );
            })}
          </FormGroup>
        </MenuList>
      </Menu>
    </Badge>
  );
}
